import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { startAuthentication } from '@simplewebauthn/browser';

const TOKEN_KEY = 'ana_terminal_token';

function api(path, options = {}) {
  return axios({
    url: path,
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Terminal-Token': localStorage.getItem(TOKEN_KEY) || ''
    }
  });
}

export default function TerminalKiosk() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [terminal, setTerminal] = useState(null);
  const [status, setStatus] = useState('Cargando terminal…');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  const loadTerminal = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setTerminal(null);
      setStatus('Terminal sin configurar');
      return false;
    }
    try {
      const response = await api('/api/terminals/kiosk/info');
      setTerminal(response.data.terminal);
      setError('');
      setStatus('Listo para identificar');
      return true;
    } catch (err) {
      setTerminal(null);
      setStatus('Terminal no autorizada');
      setError(err.response?.data?.message || 'No se pudo validar la terminal.');
      return false;
    }
  }, []);

  useEffect(() => {
    loadTerminal();
    return () => clearTimeout(timerRef.current);
  }, [loadTerminal]);

  const configure = async (event) => {
    event.preventDefault();
    const value = tokenInput.trim();
    if (!value) return;
    localStorage.setItem(TOKEN_KEY, value);
    setToken(value);
    setError('');
    await loadTerminal();
  };

  const identify = async () => {
    if (busy || !terminal) return;
    setBusy(true);
    setResult(null);
    setError('');
    setStatus('Esperando huella, rostro o PIN biométrico…');

    try {
      const optionsResponse = await api('/api/terminals/kiosk/authentication-options');
      const assertion = await startAuthentication({ optionsJSON: optionsResponse.data });
      const verifyResponse = await api('/api/terminals/kiosk/verify', {
        method: 'POST',
        data: { biometricAssertion: assertion }
      });
      const data = verifyResponse.data;
      setResult(data);
      setStatus(data.data?.type === 'Entrada' ? 'Entrada registrada' : 'Salida registrada');
      timerRef.current = setTimeout(() => {
        setResult(null);
        setStatus('Listo para identificar');
      }, 5000);
    } catch (err) {
      const message = err?.name === 'NotAllowedError'
        ? 'La identificación fue cancelada o no se completó.'
        : err.response?.data?.message || err.message || 'No fue posible registrar la asistencia.';
      setError(message);
      setStatus('Listo para identificar');
    } finally {
      setBusy(false);
    }
  };

  if (!terminal) {
    return (
      <div style={styles.shell}>
        <div style={styles.configCard}>
          <div style={styles.logo}>ANA</div>
          <h1 style={styles.title}>Configurar terminal</h1>
          <p style={styles.muted}>Introduce una sola vez el token generado desde la administración de terminales.</p>
          <form onSubmit={configure}>
            <input
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              placeholder="term_…"
              autoFocus
              style={styles.input}
            />
            <button type="submit" style={styles.primaryButton}>Activar terminal</button>
          </form>
          {error && <div style={styles.error}>{error}</div>}
          {token && <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); setTerminal(null); setError(''); }} style={styles.linkButton}>Borrar configuración local</button>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.kiosk} onClick={() => !busy && identify()}>
      <div style={styles.topbar}>
        <div><strong>ANA</strong><span style={styles.separator}>Reloj Checador</span></div>
        <div>{terminal.name} · {terminal.siteName}</div>
      </div>

      <main style={styles.main}>
        {result ? (
          <section style={styles.successCard}>
            <div style={styles.check}>✓</div>
            <div style={styles.successType}>{result.data?.type}</div>
            <div style={styles.person}>{result.user?.name || 'Empleado identificado'}</div>
            <div style={styles.time}>{new Date(result.data?.timestamp || Date.now()).toLocaleTimeString('es-MX')}</div>
            <p>{result.message}</p>
          </section>
        ) : (
          <section style={styles.readyCard}>
            <div style={styles.bioIcon}>◎</div>
            <h1 style={styles.readyTitle}>{busy ? 'Identificando…' : 'Identifícate para registrar tu asistencia'}</h1>
            <p style={styles.muted}>{busy ? 'Completa la verificación biométrica en el dispositivo.' : 'Usa la huella, rostro o autenticador biométrico disponible.'}</p>
            <button disabled={busy} onClick={identify} style={styles.primaryButton}>{busy ? 'Verificando…' : 'Identificarme'}</button>
            {error && <div style={styles.error}>{error}</div>}
          </section>
        )}
        <div style={styles.status}>{status}</div>
      </main>

      <footer style={styles.footer}>Terminal {terminal.terminalCode} · Registro protegido por ANA</footer>
    </div>
  );
}

const styles = {
  shell: { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f4f6f8', fontFamily: 'system-ui, sans-serif', padding: 24 },
  configCard: { width: 'min(520px, 100%)', background: '#fff', borderRadius: 20, padding: 32, boxShadow: '0 15px 45px rgba(0,0,0,.12)' },
  logo: { fontWeight: 800, fontSize: 26, marginBottom: 18 },
  title: { margin: '0 0 10px' },
  muted: { color: '#68727d', lineHeight: 1.5 },
  input: { width: '100%', boxSizing: 'border-box', padding: 14, border: '1px solid #cbd2d9', borderRadius: 10, fontSize: 16, margin: '16px 0' },
  primaryButton: { border: 0, borderRadius: 12, padding: '14px 24px', fontWeight: 700, fontSize: 16, cursor: 'pointer', background: '#111827', color: '#fff' },
  linkButton: { marginTop: 18, border: 0, background: 'transparent', textDecoration: 'underline', cursor: 'pointer' },
  error: { marginTop: 18, padding: 12, borderRadius: 10, background: '#fff1f2', color: '#9f1239' },
  kiosk: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f4f6f8', fontFamily: 'system-ui, sans-serif', color: '#111827' },
  topbar: { display: 'flex', justifyContent: 'space-between', padding: '18px 28px', background: '#fff', borderBottom: '1px solid #e5e7eb' },
  separator: { marginLeft: 14, color: '#6b7280' },
  main: { flex: 1, display: 'grid', placeItems: 'center', padding: 24 },
  readyCard: { width: 'min(680px, 100%)', textAlign: 'center', background: '#fff', borderRadius: 28, padding: '60px 30px', boxShadow: '0 20px 60px rgba(0,0,0,.10)' },
  bioIcon: { fontSize: 86, lineHeight: 1, marginBottom: 20 },
  readyTitle: { fontSize: 'clamp(28px, 4vw, 48px)', margin: '0 0 14px' },
  status: { marginTop: 24, color: '#6b7280' },
  successCard: { width: 'min(680px, 100%)', textAlign: 'center', background: '#fff', borderRadius: 28, padding: '54px 30px', boxShadow: '0 20px 60px rgba(0,0,0,.10)' },
  check: { fontSize: 64, fontWeight: 800 },
  successType: { fontSize: 38, fontWeight: 800, marginTop: 10 },
  person: { fontSize: 28, marginTop: 12 },
  time: { color: '#6b7280', marginTop: 8 },
  footer: { padding: 14, textAlign: 'center', color: '#7b8490', fontSize: 13 }
};
